import React from 'react';
import styled, {css} from 'styled-components';
import {Button as MfButton, useTheme} from 'modern-famly';

import {PortalModal} from 'web-app/react/components/modals/modals';
import {Flex, Base} from 'web-app/react/components/layout/layout';
import Button, {ButtonSize} from 'web-app/react/components/button-v2';
import {s0, s2, s6, s16, getSpacing} from 'web-app/styleguide/spacing';
import {Body, Subheader} from 'web-app/react/components/text/text';
import {type NonEmptyArray} from 'web-app/util/non-empty-array';
import {type InferArrayType} from 'web-app/util/array';
import {PortalRootId} from 'web-app/react/components/portals/constants';
import useTypedSelector from 'web-app/react/hooks/use-typed-selector';
import {getProperty} from 'web-app/util/typescript';

import {DialogChoice, type DialogButton, type DialogProps, type MfDialogButton} from './types';

interface InputProps {
    handleClose: (id: string) => void;
    id: string;
}

const animationDuration = 200;
const Dialog: React.FC<React.PropsWithChildren<InputProps>> = ({id, handleClose}) => {
    const {dialog} = useTypedSelector(state => ({
        dialog: state.dialog.dialogs.get(id)!,
    }));

    const [mounted, setMounted] = React.useState(false);

    const closeModal = React.useCallback(() => {
        setMounted(false);
        setTimeout(() => handleClose(id), animationDuration);
    }, [handleClose, id]);

    const handleCancel = React.useCallback(() => {
        if (dialog.choices.some(choice => choice.value === DialogChoice.CANCEL)) {
            dialog.resolve(DialogChoice.CANCEL);
            closeModal();
        }
    }, [dialog, closeModal]);

    const handleOk = React.useCallback(() => {
        if (dialog.choices.some(choice => choice.value === DialogChoice.OK)) {
            dialog.resolve(DialogChoice.OK);
            closeModal();
        }
    }, [dialog, closeModal]);

    const handleKeyUp = React.useCallback(
        (e: KeyboardEvent) => {
            switch (e?.keyCode) {
                case 13: // Enter
                    handleOk();
                    e.stopPropagation();
                    return;
                case 27: // Escape
                    handleCancel();
                    e.stopPropagation();
                    return;
                default:
                    return;
            }
        },
        [handleOk, handleCancel],
    );

    React.useEffect(() => {
        setTimeout(() => setMounted(true));

        document.addEventListener('keyup', handleKeyUp);

        return () => document.removeEventListener('keyup', handleKeyUp);
    }, [handleKeyUp]);

    const handleChoice = React.useCallback(
        (choice: InferArrayType<typeof dialog['choices']>['value']) => {
            if (dialog.onDialogChoice) {
                dialog.onDialogChoice(choice);
            }
            dialog.resolve(choice);
            closeModal();
        },
        [dialog, closeModal],
    );

    return <DialogRenderer {...dialog} mounted={mounted} handleCancel={handleCancel} handleChoice={handleChoice} />;
};

export default Dialog;

const StyledDialogModal = styled(({className, ...rest}) => (
    <PortalModal containerClassName={className} {...rest} />
))<any>`
    padding-left: 16px;
    padding-right: 16px;
    z-index: 9999;
    &,
    .inner-modal {
        opacity: 0;
        transition: opacity ${animationDuration}ms ${props => props.theme.timings.functions.default};
        ${props =>
            props.mounted
                ? css`
                      opacity: 1;
                  `
                : ''}
    }

    .inner-modal {
        max-width: 450px;
        margin-top: 110px;
        box-shadow: ${props => props.theme.shadow.five};
    }
`;

const StyledButton = styled(Button)``;

const ChoicesContainer = styled(Flex)`
    ${StyledButton}:not(:first-child) {
        margin-left: ${getSpacing(s2)};
    }
`;

const MfChoicesContainer = styled(Flex)`
    flex-direction: row;
`;

type DeprecatedButtonChoices<T> = {
    type?: never;
    choices: NonEmptyArray<DialogButton<T>>;
};

type MfButtonChoices<T> = {
    type: 'mf';
    choices: NonEmptyArray<MfDialogButton<T>>;
};

type DialogRendererBaseProps<T> = Omit<DialogProps<T>, 'resolve' | 'choices'> & {
    mounted: boolean;
    handleCancel?: VoidFunction;
    handleChoice: (choice: T) => void;
    image?: string;
    'data-e2e-id'?: string;
};

type DialogRendererProps<T> =
    | (DialogRendererBaseProps<T> & DeprecatedButtonChoices<T>)
    | (DialogRendererBaseProps<T> & MfButtonChoices<T>);

export function DialogRenderer<T extends string>(props: DialogRendererProps<T>) {
    const {mounted, handleCancel, className, title, description, children, choices, handleChoice, image, type} = props;

    const mfTheme = useTheme();
    return (
        <StyledDialogModal
            rootId={PortalRootId.contractPortalRoot}
            fullScreenOnMobile={false}
            mounted={mounted}
            onCloseModal={handleCancel}
            className={className}
            data-e2e-id={getProperty(props, 'data-e2e-id')}
        >
            {image ? (
                <Flex padding={[s6, s16, s0]} justify="center">
                    <img src={image} />
                </Flex>
            ) : null}
            <Base padding={s6} paddingTop="40px">
                {title ? <Subheader emphasized>{title}</Subheader> : null}
                {description ? (
                    <Body
                        forwardedAs={typeof description === 'string' || typeof description === 'number' ? 'p' : 'div'}
                        marginTop={title ? s2 : undefined}
                    >
                        {description}
                    </Body>
                ) : null}
                {children ? <Base marginTop={s2}>{children}</Base> : null}
                {type !== 'mf' ? (
                    <ChoicesContainer justify="flex-end" marginTop={mfTheme.spacing(8)}>
                        {choices.map(choice => (
                            <StyledButton
                                key={choice.value}
                                data-e2e-id={choice.e2eId}
                                text={choice.text}
                                onClick={() => handleChoice(choice.value)}
                                appearance={choice.appearance}
                                size={ButtonSize.Small}
                            />
                        ))}
                    </ChoicesContainer>
                ) : (
                    <MfChoicesContainer
                        childSpacing={mfTheme.spacing(2)}
                        justify="flex-end"
                        marginTop={mfTheme.spacing(8)}
                    >
                        {choices.map(choice => (
                            <MfButton
                                key={choice.value}
                                data-e2e-id={choice.e2eId}
                                text={choice.text}
                                onClick={() => handleChoice(choice.value)}
                                variant={choice.variant}
                                size={choice.size}
                            />
                        ))}
                    </MfChoicesContainer>
                )}
            </Base>
        </StyledDialogModal>
    );
}
